import { css, styled } from 'canoe';
import { ENDPOINTS, useActions } from 'hammock';
import React, { ReactElement, useContext, useEffect, useState } from 'react';

import { CollectionDefinition, CollectionFieldType } from '@candu-ai/collections/types';
import Node from 'craft/components/Node';
import Placeholder from 'craft/components/Placeholder';
import { CollectionContextMenu } from 'craft/components/SelectedToolbar/Collections';
import { BuiltInComponents } from 'craft/constants';
import { CollectionContext } from 'craft/contexts/CollectionContext';
import { useEditor, useNodeContext } from 'craft/hooks';
import { CollectionTargetLocation, CustomNodeData, Resolver } from 'craft/types';
import { hasDescendants } from 'craft/utils';

import { CollectionChildren } from './CollectionChildren';
import { CollectionToolbox } from './CollectionToolbox';

interface Props {
  children?: ReactElement | ReactElement[];
  placeholder?: ReactElement;
  custom?: CustomNodeData;
  collectionId: string;
}

const CollectionNode = styled(Node)<{ isEmpty?: boolean }>`
  ${({ theme, isEmpty }) => css`
    ${isEmpty &&
    css`
      gap: ${theme.spaces.xs};
      flex-grow: initial !important;

      & > div {
        min-height: auto;
      }
    `}
  `}
`;

interface PlaceholderWithContextProps {
  placeholder?: React.ReactNode;
  collection?: CollectionDefinition;
}

const PlaceholderWithContext: React.FC<PlaceholderWithContextProps> = ({
  placeholder,
  collection,
}: PlaceholderWithContextProps) => {
  const collectionData = useContext(CollectionContext);
  if (placeholder) {
    return <>{placeholder}</>;
  }

  const primaryField = collection?.schema.fields.find((field) => field.isPrimaryField);
  return (
    <Placeholder
      labelContent={
        collection && primaryField
          ? `${collection.collectionName}: ${collectionData.record[primaryField.id]}`
          : ``
      }
    />
  );
};

export const Collection: Resolver = ({ children, placeholder, collectionId }: Props) => {
  const hasChildren = hasDescendants(children);
  const { id: nodeId } = useNodeContext();
  const [collection, setCollection] = useState<CollectionDefinition>();
  const [isNewCollection] = useState<boolean>(!hasChildren);
  const { query, actions } = useEditor();

  const { get: getCollection } = useActions(ENDPOINTS.collections);
  const placeholderComponent = placeholder || (
    <Placeholder placeholderText="Please connect to a collection." />
  );

  useEffect(() => {
    if (collectionId) {
      getCollection({
        id: collectionId,
      })
        .then((res) => {
          if (res) {
            setCollection(res as CollectionDefinition);
          }
        })
        .catch(() => {
          setCollection(undefined);
        });
    } else {
      setCollection(undefined);
    }
  }, [collectionId]);

  // first descendant that can receive a TextField should be prefilled with collection primary field
  useEffect(() => {
    if (hasChildren && collection && isNewCollection) {
      const firstMatchingDescendant: string =
        query
          ?.node(nodeId)
          ?.descendants(true)
          .find((nid: string) =>
            [
              BuiltInComponents.ListItem,
              BuiltInComponents.Typography,
              BuiltInComponents.Button,
            ].includes(query.node(nid).node.data.name as BuiltInComponents),
          ) || '';
      if (!firstMatchingDescendant) {
        return;
      }
      const { custom } = query.node(firstMatchingDescendant).get().data;
      const primarySchemaField = collection.schema.fields.find((field) => field.isPrimaryField);

      if (!custom.collections && primarySchemaField?.type === CollectionFieldType.String) {
        actions.setCustom(firstMatchingDescendant, (customProps: any) => {
          // eslint-disable-next-line no-param-reassign
          customProps.collections = [
            {
              collectionId,
              fieldId: primarySchemaField?.id,
              targetLocation: CollectionTargetLocation.Text,
            },
          ];
        });
      }
    }
  }, [hasChildren, collection]);

  if (!collectionId) {
    return (
      <CollectionNode isEmpty data-cy="node-collection">
        {placeholderComponent}
        {placeholderComponent}
        {placeholderComponent}
        {placeholderComponent}
      </CollectionNode>
    );
  }

  return (
    <CollectionNode isEmpty={!hasChildren} data-cy="node-collection">
      <CollectionChildren collectionId={collectionId}>
        {hasChildren ? children : <PlaceholderWithContext collection={collection} />}
      </CollectionChildren>
    </CollectionNode>
  );
};

Collection.craft = {
  name: 'Collection',
  isCanvas: true,
  rules: {
    canDrop(dropTarget, self, helpers): boolean {
      // We can't drop a collection inside another collection, no matter how nested
      const nodeApi = helpers(dropTarget.id);
      const ancestors = nodeApi.ancestors(true);

      if (dropTarget.data.name === 'Collection') {
        return false;
      }

      if (ancestors.find((ancestor) => nodeApi.nodes[ancestor].data.name === 'Collection')) {
        return false;
      }

      return true;
    },
  },
};

Collection.toolbox = {
  component: [CollectionToolbox],
};

Collection.selectedToolbar = {
  customItems: [CollectionContextMenu],
};
